/**
 * ControllerStopControl — the Stop button, and the hold, for one site on a card.
 *
 * The same control as the one on the controller's own page, sized for a list.
 * It exists because the moment somebody wants it is the moment they are looking
 * at the overview watching a farm water something it should not be — and having
 * to navigate two pages deeper to find the button is the difference between
 * stopping in five seconds and stopping in thirty.
 *
 * It fetches the stop status only for a site the overview already says is held.
 * On a farm with eight controllers, asking each one on mount would be eight
 * requests to learn something the payload just told us; a site that is watering
 * normally has nothing to report until somebody presses the button, at which
 * point the hook starts polling on its own.
 */

import { useCallback } from 'react';
import type { OverviewController } from '../../services/irrigationOverview';
import { isRelayOn } from '../../services/irrigationOverview';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone } from '../../utils/timezone';
import useWateringStop from '../../hooks/useWateringStop';
import StopWateringButton from './StopWateringButton';
import WateringHoldBanner from './WateringHoldBanner';

interface ControllerStopControlProps {
  controller: OverviewController;
  /** Name the button when a farm has more than one site to tell apart. */
  showName?: boolean;
  /** Called after a stop or resume lands, so the page can re-read the farm. */
  onChanged?: () => void;
}

export default function ControllerStopControl({
  controller,
  showName = false,
  onChanged,
}: ControllerStopControlProps) {
  const orgTz = useOrgTimezone();
  const { hasAccess: canResume } = useModuleLicense('irrigation', 'admin');
  const pausedInPayload = Boolean(controller.watering_paused_at);
  const { status, busy, error, stop, resume } = useWateringStop(controller.controller_id, {
    enabled: pausedInPayload,
  });

  // Counted off the reported bitmask, which is what is actually open — not off
  // the zones the site has been configured with.
  const openValves = controller.routers.reduce((total, r) => {
    let n = 0;
    for (let i = 0; i < 8; i++) if (isRelayOn(r.relay_states, i)) n++;
    return total + n;
  }, 0);

  const handleStop = useCallback(async (reason?: string) => {
    await stop(reason);
    onChanged?.();
  }, [stop, onChanged]);

  const handleResume = useCallback(async () => {
    await resume();
    onChanged?.();
  }, [resume, onChanged]);

  const name = controller.controller_name || controller.controller_id;

  // The hold as the server last described it, falling back to what the overview
  // payload carried. The fallback is what keeps a held site legible in the
  // moment between the page loading and the status arriving — without it the
  // card offers a Stop button for a site that is already stopped.
  const held = status ?? (pausedInPayload
    ? {
        controller_id: controller.controller_id,
        transport: controller.transport || 'espnow',
        paused: true,
        paused_at: controller.watering_paused_at,
        paused_by: null,
        pause_reason: controller.watering_pause_reason,
        request: null,
      }
    : null);

  if (held?.paused || held?.request) {
    return (
      <WateringHoldBanner
        status={held}
        timezone={orgTz}
        canResume={canResume}
        busy={busy}
        onResume={handleResume}
        className="w-full"
      />
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <StopWateringButton
        controllerName={name}
        openValves={openValves}
        busy={busy}
        onStop={handleStop}
      />
      {showName && <span className="text-xs text-gray-500">{name}</span>}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}
